/**
 * Loader Component
 *
 * A simple loading spinner with optional overlay and message.
 */

import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/utils/cn';

// ============================================
// Loader Variants
// ============================================

const loaderVariants = cva('animate-spin text-primary', {
  variants: {
    size: {
      sm: 'h-4 w-4',
      md: 'h-6 w-6',
      lg: 'h-8 w-8',
      xl: 'h-12 w-12',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

// ============================================
// Loader Props
// ============================================

export interface LoaderProps extends VariantProps<typeof loaderVariants> {
  /** Additional class names */
  className?: string;
  /** Message to display below spinner */
  message?: string;
  /** Show as full-page overlay */
  fullPage?: boolean;
  /** Show as section overlay (positioned absolute) */
  overlay?: boolean;
}

// ============================================
// Loader Component
// ============================================

export function Loader({ size, className, message, fullPage, overlay }: LoaderProps) {
  const spinner = (
    <div className="flex flex-col items-center justify-center gap-3">
      <Loader2 className={cn(loaderVariants({ size }), className)} />
      {message && (
        <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">{message}</p>
      )}
    </div>
  );

  // Full page overlay
  if (fullPage) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm dark:bg-background-dark/80">
        {spinner}
      </div>
    );
  }

  // Section overlay
  if (overlay) {
    return (
      <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 dark:bg-background-dark/60">
        {spinner}
      </div>
    );
  }

  // Inline spinner
  return spinner;
}

// ============================================
// Page Loader - Convenience Component
// ============================================

export function PageLoader({ message = 'Loading...' }: { message?: string }) {
  return <Loader fullPage size="lg" message={message} />;
}

// ============================================
// Section Loader - Convenience Component
// ============================================

export function SectionLoader({ message }: { message?: string }) {
  return <Loader overlay size="md" message={message} />;
}

// ============================================
// Inline Loader - Convenience Component
// ============================================

export function InlineLoader({
  size = 'sm',
  className,
}: {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  return <Loader2 className={cn(loaderVariants({ size }), className)} />;
}
